import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Mail, Send, AlertTriangle, CheckCircle, TrendingUp, TrendingDown, Minus,
  Briefcase, Heart, Calendar, Target, Trophy, ClipboardList, Users, Shield
} from 'lucide-react';

const TrendIcon = ({ trend }) => {
  if (trend === 'improving') return <TrendingUp size={14} className="text-emerald-500" />;
  if (trend === 'declining') return <TrendingDown size={14} className="text-red-500" />;
  return <Minus size={14} className="text-slate-400" />;
};

function ClientCard({ client }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={`p-4 rounded-xl border transition-all ${client.needs_attention ? 'border-amber-200 bg-amber-50/30' : 'border-border/50 bg-white'}`} data-testid={`digest-client-${client.id}`}>
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${client.needs_attention ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-700'}`}>
            {client.name?.charAt(0)}
          </div>
          <div>
            <p className="text-sm font-semibold">{client.name}</p>
            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
              <span>{client.checkins}/{7} check-ins</span>
              <span>Mood: {client.avg_mood}/10</span>
              <TrendIcon trend={client.mood_trend} />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {client.pending_approvals > 0 && <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">{client.pending_approvals} pending</Badge>}
          {client.needs_attention && <Badge className="text-[10px] bg-amber-500 text-white">Needs Attention</Badge>}
          {!client.needs_attention && <Badge className="text-[10px] bg-emerald-500 text-white">On Track</Badge>}
        </div>
      </div>
      {expanded && (
        <div className="mt-3 pt-3 border-t border-border/50 space-y-2">
          {client.alerts?.length > 0 && (
            <div className="space-y-1">{client.alerts.map((a, i) => (
              <div key={`item-${i}`} className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg p-2"><AlertTriangle size={12} /> {a}</div>
            ))}</div>
          )}
          <div className="grid grid-cols-3 gap-2">
            <div className="text-center p-2 rounded-lg bg-muted/30"><p className="text-lg font-bold text-emerald-600">{client.sober_days}</p><p className="text-[10px] text-muted-foreground">Sober Days</p></div>
            <div className="text-center p-2 rounded-lg bg-muted/30"><p className="text-lg font-bold text-sky-600">{client.work_days}</p><p className="text-[10px] text-muted-foreground">Work Days</p></div>
            <div className="text-center p-2 rounded-lg bg-muted/30"><p className="text-lg font-bold text-violet-600">{client.support_sessions}</p><p className="text-[10px] text-muted-foreground">Support</p></div>
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Goals: {client.completed_goals} completed, {client.active_goals} active</span>
            <span>Achievements: {client.new_achievements} new</span>
          </div>
          <div className="text-[10px] text-muted-foreground">Last check-in: {client.last_checkin}</div>
        </div>
      )}
    </div>
  );
}

function ParticipantCard({ participant }) {
  const [expanded, setExpanded] = useState(false);
  const complianceColor = participant.compliance_rate >= 70 ? 'emerald' : participant.compliance_rate >= 40 ? 'amber' : 'red';
  return (
    <div className={`p-4 rounded-xl border transition-all ${participant.has_concerns ? 'border-red-200 bg-red-50/20' : 'border-border/50 bg-white'}`} data-testid={`digest-participant-${participant.id}`}>
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold bg-${complianceColor}-100 text-${complianceColor}-700`}>
            {participant.compliance_rate}%
          </div>
          <div>
            <p className="text-sm font-semibold">{participant.name}</p>
            <p className="text-[10px] text-muted-foreground">{participant.checkins} check-ins &middot; Last: {participant.last_checkin}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {participant.active_flags > 0 && <Badge variant="destructive" className="text-[10px]">{participant.active_flags} flag(s)</Badge>}
          {participant.has_concerns ? <Badge className="text-[10px] bg-red-500 text-white">Concerns</Badge> : <Badge className="text-[10px] bg-emerald-500 text-white">Compliant</Badge>}
        </div>
      </div>
      {expanded && (
        <div className="mt-3 pt-3 border-t border-border/50 space-y-2">
          {participant.concerns?.length > 0 && (
            <div className="space-y-1">{participant.concerns.map((c, i) => (
              <div key={`item-${i}`} className="flex items-center gap-2 text-xs text-red-700 bg-red-50 rounded-lg p-2"><AlertTriangle size={12} /> {c}</div>
            ))}</div>
          )}
          <div className="grid grid-cols-4 gap-2">
            <div className="text-center p-2 rounded-lg bg-muted/30"><p className="text-lg font-bold text-emerald-600">{participant.sober_days}</p><p className="text-[10px] text-muted-foreground">Sober</p></div>
            <div className="text-center p-2 rounded-lg bg-muted/30"><p className="text-lg font-bold text-sky-600">{participant.work_days}</p><p className="text-[10px] text-muted-foreground">Working</p></div>
            <div className="text-center p-2 rounded-lg bg-muted/30"><p className="text-lg font-bold text-amber-600">{participant.roadmap_completed}</p><p className="text-[10px] text-muted-foreground">Milestones</p></div>
            <div className="text-center p-2 rounded-lg bg-muted/30"><p className="text-lg font-bold text-violet-600">{participant.new_achievements}</p><p className="text-[10px] text-muted-foreground">Achievements</p></div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DigestPage() {
  const { user, axiosAuth } = useAuth();
  const [peerDigest, setPeerDigest] = useState(null);
  const [courtDigest, setCourtDigest] = useState(null);
  const [days, setDays] = useState('7');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const api = axiosAuth();
  const isPeer = user.role === 'peer_specialist';
  const isCourt = user.role === 'court_officer';
  const isAdmin = user.role === 'admin' || user.role === 'coordinator';

  useEffect(() => { fetchDigest(); }, [days]);

  const fetchDigest = async () => {
    setLoading(true);
    try {
      if (isPeer || isAdmin) {
        const r = await api.get(`/digest/peer-specialist?days=${days}`);
        setPeerDigest(r.data);
      }
      if (isCourt || isAdmin) {
        const r = await api.get(`/digest/referral-source?days=${days}`);
        setCourtDigest(r.data);
      }
    } catch {}
    setLoading(false);
  };

  const sendAll = async () => {
    setSending(true);
    try { const r = await api.post('/digest/send-all'); toast.success(r.data.message); }
    catch { toast.error('Failed to send digests'); }
    setSending(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-3 border-teal-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="digest-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Weekly Digest</h1>
          <p className="text-muted-foreground text-sm mt-1">Staff summary reports for client monitoring and compliance</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[130px] h-9 rounded-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 Days</SelectItem>
              <SelectItem value="14">Last 14 Days</SelectItem>
              <SelectItem value="30">Last 30 Days</SelectItem>
            </SelectContent>
          </Select>
          {isAdmin && (
            <Button onClick={sendAll} disabled={sending} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-1.5 h-9" data-testid="send-all-digests">
              <Send size={14} /> {sending ? 'Sending...' : 'Send All Digests'}
            </Button>
          )}
        </div>
      </div>

      {isAdmin ? (
        <Tabs defaultValue="peer">
          <TabsList className="bg-muted/50 rounded-full p-1">
            <TabsTrigger value="peer" className="rounded-full text-xs gap-1.5"><Heart size={14} /> Peer Specialist</TabsTrigger>
            <TabsTrigger value="court" className="rounded-full text-xs gap-1.5"><Shield size={14} /> Referral Source</TabsTrigger>
          </TabsList>
          <TabsContent value="peer">{peerDigest && <PeerDigestView digest={peerDigest} />}</TabsContent>
          <TabsContent value="court">{courtDigest && <CourtDigestView digest={courtDigest} />}</TabsContent>
        </Tabs>
      ) : isPeer && peerDigest ? (
        <PeerDigestView digest={peerDigest} />
      ) : isCourt && courtDigest ? (
        <CourtDigestView digest={courtDigest} />
      ) : (
        <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-8 text-center text-muted-foreground">No digest data available for your role.</CardContent></Card>
      )}
    </div>
  );
}

function PeerDigestView({ digest }) {
  const s = digest.summary || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-4 text-center">
          <Users size={18} className="mx-auto text-teal-600 mb-1" />
          <p className="text-2xl font-bold">{s.total_clients || 0}</p>
          <p className="text-[10px] text-muted-foreground">Assigned Clients</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm rounded-2xl border-l-4 border-l-amber-400"><CardContent className="p-4 text-center">
          <AlertTriangle size={18} className="mx-auto text-amber-600 mb-1" />
          <p className="text-2xl font-bold text-amber-600">{s.clients_needing_attention || 0}</p>
          <p className="text-[10px] text-muted-foreground">Need Attention</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-4 text-center">
          <ClipboardList size={18} className="mx-auto text-violet-600 mb-1" />
          <p className="text-2xl font-bold">{s.total_pending_approvals || 0}</p>
          <p className="text-[10px] text-muted-foreground">Pending Approvals</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-4 text-center">
          <Trophy size={18} className="mx-auto text-amber-500 mb-1" />
          <p className="text-2xl font-bold">{s.total_new_achievements || 0}</p>
          <p className="text-[10px] text-muted-foreground">New Achievements</p>
        </CardContent></Card>
      </div>
      <Card className="border-0 shadow-sm rounded-2xl">
        <CardHeader className="pb-2"><CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Client Summaries ({digest.period_days}-Day Period)</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(digest.clients || []).map(c => <ClientCard key={c.id} client={c} />)}
          {(!digest.clients || digest.clients.length === 0) && <p className="text-sm text-muted-foreground text-center py-4">No assigned clients</p>}
        </CardContent>
      </Card>
    </div>
  );
}

function CourtDigestView({ digest }) {
  const s = digest.summary || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-4 text-center">
          <Users size={18} className="mx-auto text-sky-600 mb-1" />
          <p className="text-2xl font-bold">{s.total_participants || 0}</p>
          <p className="text-[10px] text-muted-foreground">Participants</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm rounded-2xl border-l-4 border-l-red-400"><CardContent className="p-4 text-center">
          <AlertTriangle size={18} className="mx-auto text-red-600 mb-1" />
          <p className="text-2xl font-bold text-red-600">{s.participants_with_concerns || 0}</p>
          <p className="text-[10px] text-muted-foreground">With Concerns</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-4 text-center">
          <CheckCircle size={18} className="mx-auto text-emerald-600 mb-1" />
          <p className="text-2xl font-bold">{s.avg_compliance_rate || 0}%</p>
          <p className="text-[10px] text-muted-foreground">Avg Compliance</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-4 text-center">
          <Calendar size={18} className="mx-auto text-violet-600 mb-1" />
          <p className="text-2xl font-bold">{s.total_checkins || 0}</p>
          <p className="text-[10px] text-muted-foreground">Total Check-ins</p>
        </CardContent></Card>
      </div>
      <Card className="border-0 shadow-sm rounded-2xl">
        <CardHeader className="pb-2"><CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Participant Compliance ({digest.period_days}-Day Period)</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(digest.participants || []).map(p => <ParticipantCard key={p.id} participant={p} />)}
          {(!digest.participants || digest.participants.length === 0) && <p className="text-sm text-muted-foreground text-center py-4">No participants</p>}
        </CardContent>
      </Card>
    </div>
  );
}
